"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AtelierSelector = void 0;
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
const Block_1 = require("./Block");
const pixi_js_1 = require("pixi.js");
const Global_1 = require("../Global");
const AtelierItem_1 = require("./AtelierItem");
const store_1 = require("../../../shared/store");
const arrow_simple_svg_1 = __importDefault(require("../../assets/svg/arrow_simple.svg"));
const settings_1 = require("../config/settings");
const ui_1 = require("@pixi/ui");
const lodash_1 = __importDefault(require("lodash"));
const AssetManager_1 = __importDefault(require("../managers/AssetManager"));
class AtelierSelector extends Block_1.Block {
    constructor(app, gameManager, _size) {
        var _a;
        super(app);
        this.gameManager = gameManager;
        this._size = _size;
        this.holders = new Array(AtelierSelector.Points);
        this.allitems = [];
        this.background = new pixi_js_1.Graphics();
        this.ticker = new pixi_js_1.Ticker();
        this.spinup = this.makeButton(arrow_simple_svg_1.default, 0.01);
        this.spindown = this.makeButton(arrow_simple_svg_1.default, 0.01);
        this.dragging = 0;
        this.wasdragging = false;
        this.dragStartAngle = 0;
        this.initialWheelRotation = 0;
        this.angularVelocity = 0;
        this.previousAngle = 0;
        this._enabled = true;
        this.itemPos = null;
        this.holderPos = null;
        this.rticker = 0;
        this.dragstart = (evt) => {
            this.angularVelocity = 0;
            this.dragging = 1;
            this.initialWheelRotation = this.background.rotation;
            const mousePosition = evt.getLocalPosition(this.app.stage);
            this.dragStartAngle = this.getAngle(mousePosition.x, mousePosition.y);
            this.wasdragging = false;
        };
        this.dragmove = (evt) => {
            if (this.dragging && this._enabled) {
                if (this.dragging > 1) { // This is an internal mechanism to "pad" the dragging
                    const mousePosition = evt.getLocalPosition(this.app.stage);
                    const currentAngle = this.getAngle(mousePosition.x, mousePosition.y);
                    const angleDiff = currentAngle - this.dragStartAngle;
                    this.rotate(this.initialWheelRotation + angleDiff);
                    this.angularVelocity = angleDiff - this.previousAngle;
                    if (Math.abs(this.angularVelocity) < 0.01 || Math.abs(this.angularVelocity) > 0.8) {
                        this.angularVelocity = 0;
                    }
                    this.previousAngle = angleDiff;
                    this.wasdragging = true;
                }
                this.dragging++;
            }
        };
        this.dragstop = () => {
            this.dragging = 0;
        };
        this.background.eventMode = 'static';
        this.background.on('pointerdown', this.dragstart);
        this.background.on('globalpointermove', this.dragmove);
        this.background.on('pointerup', this.dragstop);
        this.background.on('pointerupoutside', this.dragstop);
        this.addChild(this.background);
        let selected = null;
        for (let i = 0; i < this.holders.length; i++) {
            const button = new ui_1.ButtonContainer();
            this.sigs.push(button.onPress.connect(() => {
                const aitem = (button.getChildAt(0));
                if (!this.wasdragging && !this.angularVelocity && aitem) {
                    AssetManager_1.default.tickSound();
                    this.gameManager.playerManager.updateLabel({ [aitem.group.name]: (aitem.item.value) });
                    selected === null || selected === void 0 ? void 0 : selected.deselect();
                    selected = aitem;
                    selected.select();
                }
            }));
            this.background.addChild(button);
            this.holders[i] = button;
        }
        for (let i = 0; i < store_1.stores.length; i++) {
            for (let j = 0; j < store_1.stores[i].items.length; j++) {
                const item = store_1.stores[i].items[j];
                if (this.gameManager.playerManager.owns(store_1.stores[i].name, item.value)) {
                    const something = new AtelierItem_1.AtelierItem(this.app, store_1.stores[i], item);
                    this.allitems.push(something);
                    if (!selected && ((_a = this.gameManager.playerManager.data) === null || _a === void 0 ? void 0 : _a[store_1.stores[i].name]) === item.value) {
                        selected = something;
                        selected.select();
                    }
                }
            }
        }
        const spin = 0.1;
        this.sigs.push(this.spinup.onPress.connect(() => {
            AssetManager_1.default.tickSound();
            this.angularVelocity = spin;
        }));
        this.sigs.push(this.spindown.onPress.connect(() => {
            AssetManager_1.default.tickSound();
            this.angularVelocity = -spin;
        }));
        this.addChild(this.spinup, this.spindown);
        this.ticker.autoStart = true;
        this.ticker.add((delta) => {
            if (!this.dragging) {
                this.rotate(this.background.rotation + (this.angularVelocity * delta));
                // Friction
                this.angularVelocity *= Math.pow(0.945, delta);
                if (Math.abs(this.angularVelocity) < 0.001) {
                    this.angularVelocity = 0;
                }
            }
        });
        this.update();
        this.fillPalette();
    }
    rotate(rotation) {
        const nrotation = this.normalize(rotation);
        let diff = this.background.rotation - nrotation;
        if (diff > Math.PI) {
            diff -= AtelierSelector.Circle;
        }
        else if (diff < -Math.PI) {
            diff += AtelierSelector.Circle;
        }
        if (diff) {
            this.rticker += diff;
            const ticks = Math.floor(Math.abs(this.rticker) / AtelierSelector.GapSize);
            if (ticks > 0 && this.allitems.length > this.holders.length) {
                if (diff <= 0) {
                    lodash_1.default.times(ticks, () => this.tickover(true));
                }
                else {
                    lodash_1.default.times(ticks, () => this.tickover(false));
                }
                this.rticker = this.rticker % AtelierSelector.GapSize;
            }
            this.background.rotation = nrotation;
        }
    }
    tickover(left = true) {
        if (left) {
            if (this.holderPos === null || --this.holderPos < 0) {
                this.holderPos = this.holders.length - 1;
            }
            if (this.itemPos === null || ++this.itemPos >= this.allitems.length) {
                this.itemPos = 0;
            }
        }
        else { // right
            if (this.holderPos === null || ++this.holderPos >= this.holders.length) {
                this.holderPos = 0;
            }
            if (this.itemPos === null || --this.itemPos < 0) {
                this.itemPos = this.allitems.length - 1;
            }
        }
        const head = this.holders[this.holderPos];
        if (head.children.length) {
            head.removeChildAt(0);
        }
        head.addChildAt(this.allitems[this.itemPos], 0);
    }
    fillPalette() {
        lodash_1.default.times(Math.min(this.holders.length, this.allitems.length), () => this.tickover(true));
        this.rotate(-AtelierSelector.Circle / this.holders.length / 2);
    }
    normalize(rotation) {
        rotation = rotation % AtelierSelector.Circle;
        if (rotation < 0) {
            rotation += AtelierSelector.Circle;
        }
        return rotation;
    }
    set enabled(enabled) {
        this._enabled = enabled;
        if (!this._enabled) {
            this.dragstop();
        }
    }
    toRadians(angle) {
        return angle * (Math.PI / 180);
    }
    getAngle(x, y) {
        return Math.atan2(y - this.position.y, x - this.position.x);
    }
    update() {
        const border = 4;
        const pborder = (0, Global_1.px)(border);
        const pradius = (0, Global_1.px)(this._size / 2);
        const iradius = pradius - pborder;
        this.background.clear()
            .beginFill(settings_1.Settings.blackColor)
            .drawCircle(0, 0, pradius + pborder)
            .beginFill(settings_1.Settings.atelierColor)
            .drawCircle(0, 0, pradius);
        for (let i = 0; i < this.holders.length; i++) {
            const holder = this.holders[i];
            if (holder) {
                const angle = (i / this.holders.length) * 360;
                const r = this.toRadians(angle);
                const x = iradius * Math.cos(r);
                const y = iradius * Math.sin(r);
                holder.rotation = r + Math.PI;
                holder.position.set(x, y);
            }
        }
        this.spinup.scale.set((0, Global_1.px)(0.16));
        this.spinup.rotation = this.toRadians(45);
        this.spinup.position.set(-(0, Global_1.px)(70), this.background.height / 2 - (0, Global_1.px)(10));
        this.spindown.scale.set((0, Global_1.px)(0.16));
        this.spindown.rotation = this.toRadians(194);
        this.spindown.position.set(-(0, Global_1.px)(20), this.background.height / 2 + (0, Global_1.px)(13));
    }
    get size() {
        return this._size;
    }
    set size(size) {
        this._size = size;
        this.layout();
    }
    destroy(_options) {
        this.background.off('pointerdown', this.dragstart);
        this.background.off('globalpointermove', this.dragmove);
        this.background.off('pointerup', this.dragstop);
        this.background.off('pointerupoutside', this.dragstop);
        this.ticker.destroy();
        this.allitems.forEach((a) => a.destroy());
        this.background.children.forEach((c) => {
            c.destroy(_options);
        });
        super.destroy(_options);
    }
    layout() {
        super.layout();
        this.update();
    }
}
exports.AtelierSelector = AtelierSelector;
AtelierSelector.Points = 8;
AtelierSelector.GapSize = (Math.PI * 2) / AtelierSelector.Points;
AtelierSelector.Circle = Math.PI * 2;
